import React, { useState, useEffect, useRef } from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { dashboardApi } from '../../services/dashboardApi';
import { X, FileText, Calendar, Camera, Trash2 } from 'lucide-react';
import { compressImage } from '../../core/imageUtils';

const EditEntryModal = ({ entry, onClose, onSuccess }) => {
    const { t } = useLang();
    const fileInputRef = useRef(null);
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [mode, setMode] = useState('cash'); 
    const [date, setDate] = useState('');
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [currentImageUrl, setCurrentImageUrl] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (entry) {
            const amtValue = entry.amount ? parseFloat(entry.amount).toString() : '';
            setAmount(amtValue);
            setNote(entry.note || '');
            setMode(entry.payment_mode || entry.mode || 'cash');
            const entryDate = new Date(entry.date || entry.entry_date);
            setDate(entryDate.toISOString().split('T')[0]);
            setCurrentImageUrl(entry.bill_image);
        }
    }, [entry]);

    const themeColor = (entry?.type === 'in' || entry?.entry_type === 'in') ? '#16A34A' : '#DC2626';

    const handleKeyPress = (key) => {
        if (key === 'back') setAmount(prev => prev.slice(0, -1));
        else if (key === '.' && amount.includes('.')) return;
        else if (amount.length < 10) setAmount(prev => prev + key);
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            try {
                const compressed = await compressImage(file);
                setImage(compressed);
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImagePreview(reader.result);
                };
                reader.readAsDataURL(compressed);
            } catch (err) {
                console.error("Compression failed:", err);
                setImage(file);
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImagePreview(reader.result);
                };
                reader.readAsDataURL(file);
            }
        }
    };

    const handleUpdate = async () => {
        if (!amount || parseFloat(amount) <= 0) return alert("Enter valid amount");
        setSaving(true);
        try {
            const formData = new FormData();
            // IMPORTANT: Backend explicitly requires 'id' and 'amount' inside FormData
            formData.append('id', entry.id); 
            formData.append('amount', amount);
            formData.append('entry_type', entry.type || entry.entry_type);
            formData.append('payment_mode', mode);
            formData.append('note', note);
            formData.append('entry_date', date + " " + new Date().toLocaleTimeString('en-GB'));
            formData.append('_method', 'PUT'); 
            
            if (image) {
                formData.append('bill_image', image);
            }
            
            await dashboardApi.updateCashbookEntry(entry.id, formData);
            onSuccess();
        } catch (error) {
            console.error("Update error:", error);
            alert("Error updating entry: " + error.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.header}>
                    <div style={styles.headerLeft}>
                        <X size={24} style={{cursor: 'pointer'}} onClick={onClose} />
                        <span style={{fontSize: '18px', fontWeight: '700', color: themeColor}}>
                            {t(LangKeys.edit_entry)} - ₹ {amount}
                        </span>
                    </div>
                </div>
                <div style={styles.body}>
                    <div style={styles.inputCard}>
                        <span style={{fontSize: '28px', fontWeight: '700', color: themeColor}}>₹</span>
                        <div style={{...styles.amountDisplay, color: amount ? '#1E293B' : '#94A3B8'}}>{amount || '0'}</div>
                    </div>
                    <div style={styles.toggleBox}>
                        <button style={{...styles.toggleBtn, backgroundColor: mode === 'cash' ? themeColor : '#F1F5F9', color: mode === 'cash' ? '#FFF' : '#64748B'}} onClick={() => setMode('cash')}>{t(LangKeys.cash)}</button>
                        <button style={{...styles.toggleBtn, backgroundColor: mode === 'online' ? themeColor : '#F1F5F9', color: mode === 'online' ? '#FFF' : '#64748B'}} onClick={() => setMode('online')}>{t(LangKeys.online)}</button>
                    </div>
                    <div style={styles.noteBox}>
                        <FileText size={20} color="#94A3B8" />
                        <input type="text" placeholder={t(LangKeys.details)} style={styles.noteInput} value={note} onChange={(e) => setNote(e.target.value)} />
                    </div>
                    <div style={styles.metaGrid}>
                        <div style={styles.metaAction}>
                            <Calendar size={18} />
                            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={styles.dateIn} /> 
                            {date}
                        </div>
                        
                        <div style={styles.metaAction} onClick={() => fileInputRef.current.click()}>
                            {imagePreview || currentImageUrl ? (
                                <div style={styles.previewWrap}>
                                    <img src={imagePreview || currentImageUrl} alt="preview" style={styles.previewImg} />
                                    <div style={styles.removeImg} onClick={(e) => { e.stopPropagation(); setImage(null); setImagePreview(null); setCurrentImageUrl(null); }}>
                                        <X size={12} color="#FFF" />
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <Camera size={18} />
                                    <span>{t(LangKeys.attach_bill)}</span>
                                </>
                            )}
                            <input 
                                type="file" 
                                ref={fileInputRef} 
                                style={{display: 'none'}} 
                                accept="image/*" 
                                onChange={handleImageChange} 
                            />
                        </div>
                    </div>
                    <div style={styles.keypad}>
                        {['1','2','3','4','5','6','7','8','9','.','0','back'].map(k => (
                            <button key={k} style={styles.key} onClick={() => handleKeyPress(k)}>{k === 'back' ? '⌫' : k}</button>
                        ))}
                    </div>
                    <button style={{...styles.saveBtn, backgroundColor: themeColor}} onClick={handleUpdate} disabled={saving}>
                        {saving ? '...' : t(LangKeys.save).toUpperCase()}
                    </button>
                </div>
            </div>
        </div>
    );
};

const styles = {
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 4000 },
    modal: { width: '100%', maxWidth: '380px', backgroundColor: '#FFF', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' },
    header: { padding: '14px', borderBottom: '1px solid #F1F5F9' },
    headerLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
    body: { padding: '16px' },
    inputCard: { backgroundColor: '#F8FAFC', padding: '14px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid #F1F5F9' },
    amountDisplay: { fontSize: '24px', fontWeight: '800' },
    toggleBox: { display: 'flex', backgroundColor: '#F1F5F9', borderRadius: '30px', padding: '4px', margin: '14px 0' },
    toggleBtn: { flex: 1, padding: '8px', borderRadius: '26px', border: 'none', fontWeight: '700', cursor: 'pointer' },
    noteBox: { display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#F8FAFC', padding: '10px', borderRadius: '10px', border: '1px solid #F1F5F9', marginBottom: '10px' },
    noteInput: { border: 'none', background: 'transparent', outline: 'none', fontSize: '13px', flex: 1 },
    metaGrid: { display: 'flex', gap: '8px', marginBottom: '16px' },
    metaAction: { flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', position: 'relative', cursor: 'pointer', minHeight: '40px' },
    dateIn: { position: 'absolute', opacity: 0, width: '100%', height: '100%', cursor: 'pointer' },
    previewWrap: { position: 'relative', display: 'flex', alignItems: 'center', gap: '6px' },
    previewImg: { width: '24px', height: '24px', borderRadius: '4px', objectFit: 'cover' },
    removeImg: { position: 'absolute', top: '-6px', right: '-6px', backgroundColor: '#DC2626', borderRadius: '50%', width: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    keypad: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', marginBottom: '14px' },
    key: { height: '44px', borderRadius: '8px', border: 'none', backgroundColor: '#F8FAFC', fontSize: '16px', fontWeight: '700', cursor: 'pointer' },
    saveBtn: { width: '100%', padding: '14px', borderRadius: '12px', border: 'none', color: '#FFF', fontSize: '16px', fontWeight: '800', cursor: 'pointer' }
};

export default EditEntryModal;
